/**
 * Request Summary Service (FALLBACK)
 * 
 * ⚠️ This service now serves as a FALLBACK for when AI-powered backend summary generation
 * is unavailable or fails. For new code, prefer using AISummaryService which calls the
 * backend AI endpoint with automatic fallback to this service.
 * 
 * Generates rule-based summaries of booking requests by analyzing:
 * - Form type (appointment type)
 * - Question/answer pairs
 * - Project details
 * - Notes
 * - Customer preferences (date, time)
 * 
 * Backend AI Integration:
 * - Primary: Backend calls OpenAI GPT-4 for intelligent summaries
 * - Fallback 1: Backend calls Claude/Anthropic if OpenAI fails
 * - Fallback 2: This service provides rule-based summaries as last resort
 * 
 * Usage:
 * ```typescript
 * import { AISummaryService } from './aiSummaryService'; // PREFERRED
 * 
 * // Use AI service (with automatic fallback)
 * const summary = await AISummaryService.generateRequestSummary({
 *   appointmentType: 'On-Site Estimate',
 *   answers: [...],
 *   notes: 'Customer needs urgent attention'
 * });
 * 
 * // Direct fallback usage (for synchronous contexts only)
 * import { RequestSummaryService } from './requestSummaryService';
 * const cardSummary = RequestSummaryService.generateCardSummary({
 *   appointmentType: 'Repair Service',
 *   answers: [...],
 *   customerName: 'John Doe'
 * });
 * ```
 */

export interface RequestSummaryData {
  appointmentType?: string;
  answers?: Array<{ question: string; answer: string; }>;
  projectDetail?: string;
  notes?: string;
  preferredDate?: string;
  preferredTime?: string;
  customerName?: string;
}

export class RequestSummaryService {
  /**
   * Generate a concise, human-readable summary of a booking request
   */
  static generateSummary(data: RequestSummaryData): string {
    const summaryParts: string[] = [];
    
    // Start with the appointment type context
    if (data.appointmentType) {
      summaryParts.push(`${data.appointmentType} request`);
    }
    
    // Extract key information from answers
    const keyInfo = this.extractKeyInformation(data.answers || []);
    if (keyInfo.length > 0) {
      summaryParts.push(...keyInfo);
    }
    
    // Add project details if available
    if (data.projectDetail && data.projectDetail.trim()) {
      const cleanedDetail = this.cleanText(data.projectDetail);
      if (cleanedDetail && !this.isDuplicate(cleanedDetail, summaryParts)) {
        summaryParts.push(cleanedDetail);
      }
    }
    
    // Add notes if they provide additional context
    if (data.notes && data.notes.trim()) {
      const cleanedNotes = this.cleanText(data.notes);
      if (cleanedNotes && !this.isDuplicate(cleanedNotes, summaryParts)) {
        summaryParts.push(cleanedNotes);
      }
    }
    
    // Add scheduling preferences if available
    const schedulingInfo = this.formatSchedulingPreferences(data.preferredDate, data.preferredTime);
    if (schedulingInfo) {
      summaryParts.push(schedulingInfo);
    }
    
    // If we have no content, provide a default message
    if (summaryParts.length === 0) {
      return data.customerName 
        ? `${data.customerName} has submitted a booking request.`
        : 'New booking request submitted.';
    }
    
    // Combine parts into a coherent summary
    return this.formatSummary(summaryParts);
  }
  
  /**
   * Extract and format key information from question/answer pairs
   */
  private static extractKeyInformation(answers: Array<{ question: string; answer: string; }>): string[] {
    const keyInfo: string[] = [];
    const priorityKeywords = [
      'issue', 'problem', 'need', 'want', 'looking for', 'require',
      'service', 'repair', 'install', 'replace', 'fix', 'inspect',
      'location', 'address', 'property', 'building'
    ];
    
    answers.forEach(({ question, answer }) => {
      if (!answer || answer.trim() === '' || answer.toLowerCase() === 'n/a') {
        return;
      }
      
      const lowerQuestion = question.toLowerCase();
      const lowerAnswer = answer.toLowerCase();
      
      // Check if this is a high-priority question
      const isPriority = priorityKeywords.some(keyword => 
        lowerQuestion.includes(keyword) || lowerAnswer.includes(keyword)
      );
      
      // Format the answer based on question type
      if (this.isYesNoQuestion(answer)) {
        // For yes/no questions, include the question context
        if (answer.toLowerCase().startsWith('yes')) {
          keyInfo.push(this.simplifyQuestion(question));
        }
      } else if (isPriority && answer.length < 200) {
        // Include detailed answers for priority questions
        keyInfo.push(this.cleanText(answer));
      } else if (answer.length < 100) {
        // Include short, descriptive answers
        keyInfo.push(this.cleanText(answer));
      }
    });
    
    // Limit to top 3 most relevant pieces of information
    return keyInfo.slice(0, 3);
  }
  
  /**
   * Format scheduling preferences into readable text
   */
  private static formatSchedulingPreferences(preferredDate?: string, preferredTime?: string): string | null {
    if (!preferredDate && !preferredTime) {
      return null;
    }
    
    const parts: string[] = [];
    
    if (preferredDate) {
      try {
        const date = new Date(preferredDate);
        const formattedDate = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
        });
        parts.push(`preferred date: ${formattedDate}`);
      } catch {
        // If date parsing fails, use the raw value
        parts.push(`preferred date: ${preferredDate}`);
      }
    }
    
    if (preferredTime && preferredTime.toLowerCase() !== 'anytime') {
      parts.push(`preferred time: ${preferredTime}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : null;
  }
  
  /**
   * Format summary parts into a cohesive narrative
   */
  private static formatSummary(parts: string[]): string {
    if (parts.length === 0) {
      return '';
    }
    
    // Ensure first character is capitalized
    parts[0] = this.capitalizeFirstLetter(parts[0]);
    
    // Join parts intelligently
    let summary = parts[0];
    
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      const prevPart = parts[i - 1];
      
      // Determine appropriate connector
      if (part.toLowerCase().startsWith('preferred')) {
        summary += `. ${this.capitalizeFirstLetter(part)}`;
      } else if (prevPart.endsWith('.') || prevPart.endsWith('!') || prevPart.endsWith('?')) {
        summary += ` ${this.capitalizeFirstLetter(part)}`;
      } else {
        summary += `. ${this.capitalizeFirstLetter(part)}`;
      }
    }
    
    // Ensure summary ends with proper punctuation
    if (!summary.match(/[.!?]$/)) {
      summary += '.';
    }
    
    return summary;
  }
  
  /**
   * Clean and normalize text
   */
  private static cleanText(text: string): string {
    return text
      .trim()
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/\.+$/, '') // Remove trailing periods (we'll add them back in formatting)
      .replace(/^[,\s]+/, ''); // Remove leading commas and spaces
  }
  
  /**
   * Check if text is a duplicate or very similar to existing parts
   */
  private static isDuplicate(text: string, existingParts: string[]): boolean {
    const lowerText = text.toLowerCase();
    return existingParts.some(part => {
      const lowerPart = part.toLowerCase();
      // Check for exact match or substring match
      return lowerPart === lowerText || 
             lowerPart.includes(lowerText) || 
             lowerText.includes(lowerPart);
    });
  }
  
  /**
   * Check if an answer is a yes/no response
   */
  private static isYesNoQuestion(answer: string): boolean {
    const normalized = answer.toLowerCase().trim();
    return normalized === 'yes' || 
           normalized === 'no' || 
           normalized.startsWith('yes,') || 
           normalized.startsWith('no,');
  }
  
  /**
   * Simplify a question by removing question words
   */
  private static simplifyQuestion(question: string): string {
    return question
      .replace(/^(do you|are you|is there|would you|can you|have you)\s+/i, '')
      .replace(/\?$/, '')
      .toLowerCase();
  }
  
  /**
   * Capitalize the first letter of a string
   */
  private static capitalizeFirstLetter(text: string): string {
    if (!text || text.length === 0) {
      return text;
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  
  /**
   * Generate a summary for display in a card (shorter version)
   */
  static generateCardSummary(data: RequestSummaryData, maxLength: number = 150): string {
    const fullSummary = this.generateSummary(data);
    
    if (fullSummary.length <= maxLength) {
      return fullSummary;
    }
    
    // Truncate at sentence boundary if possible
    const sentences = fullSummary.match(/[^.!?]+[.!?]+/g) || [fullSummary];
    let truncated = '';
    
    for (const sentence of sentences) {
      if ((truncated + sentence).length <= maxLength) {
        truncated += sentence;
      } else {
        break;
      }
    }
    
    // If we couldn't fit even one sentence, do a hard truncate
    if (truncated.length === 0) {
      truncated = fullSummary.substring(0, maxLength - 3) + '...';
    }
    
    return truncated.trim();
  }
}

